import { Grid, Radio } from '@mui/material';
import { CreateProduct, Product } from '@thro-types/leihbar';

import { useFormikContext } from 'formik';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useProductsQuery } from 'api/hooks/leihbar';
import { Filter, Filters } from 'app/components';
import { NoItems } from 'app/components/RichList/components/NoItems';
import { TileSkeleton } from 'app/components/TilesOrListView/components/TileSkeleton';
import { translations } from 'locales/i18n';

import { ProductCard } from '../../ProductCard';

interface Props {
  productId?: number;
}

export const BundleModal = ({ productId }: Props) => {
  const { t } = useTranslation();
  const { values, setFieldValue } = useFormikContext<
    CreateProduct & { products: Product[]; categoryIds: number[] }
  >();
  const nameFilter = useState('');
  const [location, setLocation] = useState<string | undefined>(
    values.products.length > 0 ? values.products[0]?.location?.name : undefined
  );
  const { data: products } = useProductsQuery({
    params: {
      location: location,
      withoutBundles: true,
      title: nameFilter[0]?.length > 0 ? nameFilter[0] : undefined
    }
  });

  const filters: Filter[] = [
    { field: 'title', label: t(translations.common.search()), stateHandler: nameFilter }
  ];

  async function handleClick(_event, selectedProduct: Product) {
    const existingProduct = values.products?.find(product => product.id === selectedProduct.id);

    if (existingProduct) {
      // Remove the selected product from the list
      const updatedProducts = values.products?.filter(product => product.id !== selectedProduct.id);

      if (updatedProducts?.length) {
        setFieldValue('products', updatedProducts);
        setLocation(updatedProducts[0]?.location?.name);
      } else {
        // If no products remain, reset location
        setLocation(undefined);
        setFieldValue('products', []);
      }
    } else {
      // Add the selected product to the list
      const updatedProducts = [...(values.products ?? []), selectedProduct];
      setFieldValue('products', updatedProducts);

      if (selectedProduct.categories) {
        const updatedCategoryIds = [
          ...new Set([...(values.categoryIds ?? []), ...selectedProduct.categories.map(c => c.id)])
        ];
        setFieldValue('categoryIds', updatedCategoryIds);
      }

      const newLocation = selectedProduct.location;
      setFieldValue('location', newLocation);
      setLocation(newLocation?.name);
    }
  }

  const filteredProducts = products?.data.filter(product => product.id !== productId);

  return (
    <Grid container flexDirection={'column'} className="bundle-product__container">
      <Grid item>
        <Filters filterConfig={filters} />
      </Grid>
      {!filteredProducts ? (
        <Grid container>
          {Array.from({ length: 3 }).map((_, i) => (
            <TileSkeleton key={i} />
          ))}
        </Grid>
      ) : (
        <Grid container item>
          {filteredProducts.length === 0 ? (
            <NoItems message={t(translations.pages.leihbar.product.bundle.noProductsAvailable())} />
          ) : (
            filteredProducts.map(product => {
              return (
                <Grid item xs={12} md={4} style={{ position: 'relative' }} key={product.id}>
                  <ProductCard product={product} isPreview />
                  <Radio
                    className="bundle-product__radio"
                    onClick={event => handleClick(event, product)}
                    checked={!!values.products?.find(vp => vp.id === product.id)}
                    sx={{
                      '& .MuiSvgIcon-root': {
                        fontSize: 32
                      }
                    }}
                  />
                </Grid>
              );
            })
          )}
        </Grid>
      )}
    </Grid>
  );
};
